import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { ProductLevelContentAccuracySummary } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ProductLevelContentAccuracySummary';
import { ContentDropdownBarChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentBarChart';
import { ContentSplineChart } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentSplineChart';
import { ContentTableGrid } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/ContentTableGrid';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMainEntityType, useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { useContentService } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useContentService';
import {
  CategoryType,
  ContentMetricType
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { KeyMetricsFilterAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { useContentChartSubtitles } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useDynamicSubtitle';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { formatMetric } from 'src/utils/app';
import { BarChartData } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/types';
import {
  createMetricColumnDefinition,
  createProductColumn
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/utils';
import { ContentAccuracyTopSection } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV2/ContentAccuracyTopSection';

const ContentAccuracyV2Page = () => {
  const {
    getContentScoresKeyMetrics,
    getContentScoresByWeek,
    getContentScoresByProduct,
    getContentScoreByCategory,
    getProductLevelContentAccuracyDetails
  } = useContentService();

  const { generateSplineChartSubtitle, generateBarChartSubtitle, generateTableGridSubtitle } =
    useContentChartSubtitles();

  const title = 'Content Accuracy';
  const entityType = useMainEntityType();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();

  const groupByOptionsByEntityType = {
    default: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ],
    brand: [
      {
        id: 'category',
        label: 'Category',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Category)
      },
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      }
    ],
    category: [
      {
        id: 'subcategory',
        label: 'Subcategory',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Subcategory)
      },
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ],
    subcategory: [
      {
        id: 'brand',
        label: 'Brand',
        queryFn: () => getContentScoreByCategory(ContentMetricType.ContentAccuracyScore, CategoryType.Brand)
      }
    ]
  };

  const columnDefinitions = [
    ['contentAccuracyScore', 'Accuracy'],
    ['titleScore', 'Title'],
    ['featureBulletsScore', 'Bullets'],
    ['descriptionScore', 'Description'],
    ['imagesScore', 'Images'],
    ['videosScore', 'Videos']
  ].map(([id, header], index, array) => {
    if (index === array.length - 1) {
      return createMetricColumnDefinition([id, header], { cellStyle: { paddingRight: '16px' } });
    }
    return createMetricColumnDefinition([id, header]);
  });

  return (
    <BasePageContainer sx={{ display: 'flex', flexDirection: 'column', gap: `${BEACON_CHART_SPACING}px` }}>
      <ContentAccuracyTopSection
        type={ContentMetricType.ContentAccuracyScore}
        title="Key Metrics"
        queryFn={async () => {
          const keyMetrics = new KeyMetricsFilterAdapter(
            await getContentScoresKeyMetrics(
              ContentMetricType.ContentAccuracyScore,
              mainTimePeriod,
              comparisonTimePeriod
            ),
            {
              descriptionScore: 'Description',
              featureBulletsScore: 'Bullets',
              imagesScore: 'Images',
              titleScore: 'Title',
              videosScore: 'Videos'
            }
          );
          return keyMetrics;
        }}
      />

      {entityType === 'product' && (
        <ProductLevelContentAccuracySummary
          queryFn={() => getProductLevelContentAccuracyDetails()}
          renderSubtitle={() => {
            return 'The section below displays the current accuracy status at the component-level for this product.';
          }}
        />
      )}

      {entityType !== 'product' && (
        <>
          <ContentSplineChart
            title={title}
            queryFn={() =>
              getContentScoresByWeek(ContentMetricType.ContentAccuracyScore, mainTimePeriod, comparisonTimePeriod)
            }
            renderSubtitle={(score: string, change: number) => {
              return generateSplineChartSubtitle({
                formattedAverage: score,
                metricName: 'content accuracy score',
                formattedPercentChange: formatMetric(Math.abs(change), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
                direction: change > 0 ? 'higher' : 'lower'
              });
            }}
          />

          <ContentDropdownBarChart
            prefix={`${title} by`}
            options={groupByOptionsByEntityType[entityType] || groupByOptionsByEntityType.default}
            renderSubtitle={(selectedOption, data: BarChartData) => {
              const count = data.chartData[0].categories.length;

              const groupByText = (() => {
                switch (selectedOption) {
                  case 'category':
                    return count === 1 ? 'from 1 category' : `from all ${count} categories`;
                  case 'subcategory':
                    return count === 1 ? 'from 1 subcategory' : `from all ${count} subcategories`;
                  case 'brand':
                    return count === 1 ? 'from 1 brand' : `from all ${count} brands`;
                  default:
                    return '';
                }
              })();

              return generateBarChartSubtitle({
                metricName: 'content utilization score',
                groupByText
              });
            }}
          />

          <ContentTableGrid
            title={`${title} by Product`}
            productColumn={createProductColumn('461px')}
            columnDefinitions={columnDefinitions}
            queryFn={() =>
              getContentScoresByProduct(ContentMetricType.ContentAccuracyScore, mainTimePeriod, comparisonTimePeriod)
            }
            renderSubtitle={(view) => {
              return generateTableGridSubtitle({
                view,
                metricName: 'content accuracy score'
              });
            }}
          />
        </>
      )}
    </BasePageContainer>
  );
};

export default ContentAccuracyV2Page;
